// ── services/api/trees.api.ts ─────────────────────────────────────────────────
// All /trees/* calls.
// ────────────────────────────────────────────────────────────────────────────

import { api } from './client';
import type { TreeNode, Relationship, TreeTemplate } from '@/types';

export interface TreeListItem {
  id:           string;
  name:         string;
  templateType: string;
  createdAt:    string;
  updatedAt:    string;
  totalNodes:   number;
  /** True when the current user created this tree (only admins can rename/delete it). */
  isAdmin:      boolean;
}

export interface TreeDetail {
  id:            string;
  name:          string;
  templateType:  string;
  /** Same value as templateType, typed and aliased for `@/types`' `Tree.template` —
   *  every consumer that reads `Tree.template` (the layout-engine dispatch in
   *  particular) needs this populated the same way regardless of which screen
   *  fetched it first into the shared `['tree', treeId]` query cache; see
   *  `get()` below for why this can't be left to each caller to remember. */
  template:      TreeTemplate;
  clusterAxisField?: 'LAST_NAME' | 'VILLAGE';
  rootNodeId?:   string | null;
  adminUserIds?: string[];
  nodes:         TreeNode[];
  relationships: Relationship[];
  stats: {
    totalNodes:      number;
    claimedNodes:    number;
    shadowNodes:     number;
    generationDepth: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AddNodePayload {
  firstName:         string;
  lastName?:         string;
  nativeName?:       string;
  gender?:           'MALE' | 'FEMALE' | 'OTHER' | 'UNKNOWN';
  dateOfBirth?:      string;
  birthYearApprox?:  number;  // SAMAJ fallback, used only when dateOfBirth is unset
  village?:          string;  // required for SAMAJ nodes
  villagePincode?:   string;
  isDeceased?:       boolean;
  generationDepth?:  number;
  birthOrder?:       number | null;  // signed offset from anchor sibling: negative=elder/left, positive=younger/right, null=unknown
  // Auto-link a relationship to an existing node in the same call
  relativeNodeId?:   string;
  relationshipType?: string;
}

export type InviteStatus = 'NONE' | 'PENDING' | 'EXPIRED' | 'CLAIMED';

export interface NodeInviteStatus {
  nodeId:      string;
  displayName: string;
  avatarUrl?:  string | null;
  status:      InviteStatus;
  expiresAt?:  string;
}

export interface AddRelationshipPayload {
  sourceNodeId:            string;
  targetNodeId:            string;
  relationType:            string;
  isMarriageActive?:       boolean;
  marriageDate?:           string;
  divorceDate?:            string;
  // Opt out of the backend's "single active spouse" auto-link guess when a
  // PARENT_OF edge is added (used when the caller already resolved the
  // other-parent question itself, e.g. via an explicit picker).
  autoLinkSpouseAsParent?: boolean;
}

export const treesApi = {
  /** Get all trees for a user */
  list: (userId: string) =>
    api.get<{ data: TreeListItem[]; total: number }>(`/users/${userId}/trees`),

  /** Get full tree with nodes + relationships */
  get: async (treeId: string): Promise<TreeDetail> => {
    const data = await api.get<TreeDetail>(`/trees/${treeId}`);
    // The backend only ever sends templateType (see tree.service.ts) — mapped
    // here, once, so every caller sharing the ['tree', treeId] query cache
    // key gets the same fully-populated shape regardless of which screen's
    // fetch happens to populate the cache first. See TreeDetail.template's
    // own comment for why this can't be left to individual callers.
    return { ...data, template: data.templateType as TreeDetail['template'] };
  },

  /** Create a new tree */
  create: (payload: {
    name: string;
    templateType?: string;
    clusterAxisField?: 'LAST_NAME' | 'VILLAGE'; // required when templateType is SAMAJ
    selfNode?: {
      firstName: string;
      lastName?: string;
      gender?: string;
      dateOfBirth?: string;
      birthYearApprox?: number;
      village?: string;
      villagePincode?: string;
    };
  }) => api.post<TreeDetail>('/trees', payload),

  /** Rename a tree (admin only) */
  rename: (treeId: string, name: string) =>
    api.patch<TreeDetail>(`/trees/${treeId}`, { name }),

  /** Change a SAMAJ tree's lineage-cluster grouping (admin only) — a display
   *  recompute, not a structural change, so this is safe to change any time. */
  updateClusterAxisField: (treeId: string, clusterAxisField: 'LAST_NAME' | 'VILLAGE') =>
    api.patch<TreeDetail>(`/trees/${treeId}`, { clusterAxisField }),

  /** Delete a tree (admin only) — cascades to its nodes/relationships */
  remove: (treeId: string) =>
    api.delete<{ success: boolean }>(`/trees/${treeId}`),

  /** Add a node to a tree */
  addNode: (treeId: string, payload: AddNodePayload) =>
    api.post<TreeNode>(`/trees/${treeId}/nodes`, payload),

  /** Update a node */
  updateNode: (treeId: string, nodeId: string, payload: Partial<AddNodePayload>) =>
    api.patch<TreeNode>(`/trees/${treeId}/nodes/${nodeId}`, payload),

  /** Delete a node */
  deleteNode: (treeId: string, nodeId: string) =>
    api.delete<{ success: boolean }>(`/trees/${treeId}/nodes/${nodeId}`),

  /** Add a relationship */
  addRelationship: (treeId: string, payload: AddRelationshipPayload) =>
    api.post<Relationship>(`/trees/${treeId}/relationships`, payload),

  /** Update a relationship (e.g. mark a marriage as divorced) */
  updateRelationship: (treeId: string, relId: string, payload: {
    isMarriageActive?: boolean;
    marriageDate?:     string | null;
    divorceDate?:      string | null;
  }) => api.patch<Relationship>(`/trees/${treeId}/relationships/${relId}`, payload),

  /** Delete a relationship */
  deleteRelationship: (treeId: string, relId: string) =>
    api.delete<{ success: boolean }>(`/trees/${treeId}/relationships/${relId}`),

  /** Get paginated audit history */
  getHistory: (treeId: string, page = 1) =>
    api.get<{ data: unknown[]; total: number }>(`/trees/${treeId}/history?page=${page}`),

  /** Invite status for every node in the tree (admin only) */
  getInvites: (treeId: string) =>
    api.get<{ data: NodeInviteStatus[] }>(`/trees/${treeId}/invites`),

  /** Find shortest path between two nodes */
  findPath: (treeId: string, fromNodeId: string, toNodeId: string) =>
    api.get<{ path: string[]; distance: number | null; connected: boolean }>(
      `/trees/${treeId}/path?from=${fromNodeId}&to=${toNodeId}`
    ),
};
